import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { typedDoc, getDocument } from '@/lib/firebase/firestore'
import { toUserFacingError, withErrorCode } from '@/lib/errors'
import { useAuth } from '@/hooks/useAuth'
import type { ClientEngagementDocument } from '@/types'
import { BoardScreen } from '../components/BoardScreen'

export function AdminProjectBoardPage() {
  const { engagementId } = useParams<{ engagementId: string }>()
  const { user } = useAuth()
  const currentUserId = user?.uid ?? ''

  const [engagementLoading, setEngagementLoading] = useState(true)
  const [engagementError, setEngagementError] = useState<Error | null>(null)
  const [rootBoardId, setRootBoardId] = useState<string | null>(null)
  const [engagementLineItems, setEngagementLineItems] = useState<
    ClientEngagementDocument['lineItems'] | undefined
  >(undefined)

  useEffect(() => {
    if (!engagementId) return
    let cancelled = false
    setEngagementLoading(true)
    ;(async () => {
      try {
        const ref = typedDoc<ClientEngagementDocument>('clientEngagements', engagementId)
        const eng = await getDocument(ref)
        if (cancelled) return
        setRootBoardId(eng?.boardId ?? null)
        setEngagementLineItems(eng?.lineItems)
        setEngagementError(null)
      } catch (err) {
        if (!cancelled) {
          setEngagementError(toUserFacingError(
            err,
            'We could not load this engagement. Please try again.',
            'project-board/engagement-load',
          ))
        }
      } finally {
        if (!cancelled) setEngagementLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [engagementId])

  if (!engagementId) {
    return (
      <div className="p-6 text-sm text-destructive">
        {withErrorCode('Missing engagement id.', 'project-board/missing-engagement-id')}
      </div>
    )
  }

  if (engagementLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading engagement…</div>
  }

  if (engagementError) {
    return <div className="p-6 text-sm text-destructive">{engagementError.message}</div>
  }

  if (!rootBoardId) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/admin/client-services/engagements">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to engagements
          </Button>
        </Link>
        <p className="text-sm text-muted-foreground">
          This engagement has no project board yet. Use the Create board action on the
          engagements list.
        </p>
      </div>
    )
  }

  return (
    <BoardScreen
      rootBoardId={rootBoardId}
      currentUserId={currentUserId}
      engagementLineItems={engagementLineItems}
      showBackLink={true}
    />
  )
}

export default AdminProjectBoardPage
